// FLAGS: identity
// The six API calls that establish, upgrade and end a scheduler's session. Nothing else in the app
// talks to the auth endpoints, and no domain (event/invitee) endpoints exist here yet.
import { isAxiosError } from "axios";
import apiClient from "@src/lib/apiClient";
import { LoginCodeFlowResponse, UserProfile } from "@src/models/auth";

/**
 * Selects the `GoogleSettings.GoogleSpacesClient` OAuth client on the backend
 * (RequestSource.IsSpaces) — the dedicated OpenUp Spaces (Convene) client.
 */
const SPACES_REQUEST_SOURCE = "IsSpaces";

const getDomain = (): string => {
  let domain = globalThis.location.hostname;
  if (globalThis.location.port && globalThis.location.port !== "443") {
    domain = `${domain}:${globalThis.location.port}`;
  }
  return domain;
};

/**
 * FLAGS: identity
 * `requestSource` is what makes the backend pick the dedicated Spaces OAuth client;
 * changing it moves the exchange to a client whose redirect URIs do not include this
 * origin, so every sign-in fails at Google rather than here.
 * Exchanges the Google auth-code for MOU JWT cookies.
 * A 401 here means "bad login", not "expired session" — skipAuthRefresh.
 */
export const loginWithGoogleCode = async (
  code: string,
): Promise<LoginCodeFlowResponse> => {
  const { data } = await apiClient.post<LoginCodeFlowResponse>(
    "/v2/external-login/login-code-flow",
    {
      code,
      provider: "Google",
      domain: getDomain(),
      requestSource: SPACES_REQUEST_SOURCE,
    },
    { skipAuthRefresh: true },
  );
  return data;
};

/**
 * Signs up a new CustomerSuccess account with the Google auth-code (mirrors
 * OpenUp.Admin's register flow). The backend creates the account and sets
 * login cookies, but the account has no CustomerSuccess role yet — an admin
 * must grant it — so the caller surfaces the "contact your administrator"
 * message rather than entering the app. The response body isn't needed.
 * A 401/403 here means "bad sign-up", not "expired session" — skipAuthRefresh.
 */
export const registerWithGoogleCode = async (code: string): Promise<void> => {
  await apiClient.post(
    "/v2/external-login/register-code-flow",
    {
      code,
      provider: "Google",
      domain: getDomain(),
      requestSource: SPACES_REQUEST_SOURCE,
    },
    { skipAuthRefresh: true },
  );
};

/**
 * FLAGS: identity
 * The second factor is enforced by the API, not by this call site: skipping this step
 * does not widen access, it leaves a session the gated endpoints keep refusing.
 *
 * Verifies the authenticator (TOTP) code and upgrades the session token so it
 * satisfies CustomerSuccessPolicy (adds mfa_verified_by=AuthenticatorKey).
 */
export const validateTotp = async (code: string): Promise<void> => {
  await apiClient.post(
    "/v1/users/2fa/validate",
    { code, method: "AuthenticatorKey" },
    { skipAuthRefresh: true },
  );
};

/**
 * FLAGS: identity
 * Only 401 and 403 become `null`; anything else still throws. Widening that catch
 * would make a backend outage indistinguishable from a signed-out user, and both
 * route guards would silently send working sessions to /login.
 * Bootstrap whoami — same endpoint OpenUp.Admin and the CS dashboard gate
 * roles on. A 401/403 on app init means "not logged in" (or 2FA not yet
 * verified) — return null instead of throwing so the refresh interceptor
 * never spirals.
 */
export const getUserProfile = async (): Promise<UserProfile | null> => {
  try {
    const { data } = await apiClient.get<UserProfile>("/v2/users/profile", {
      skipAuthRefresh: true,
    });
    return data;
  } catch (error) {
    if (
      isAxiosError(error) &&
      (error.response?.status === 401 || error.response?.status === 403)
    ) {
      return null;
    }
    throw error;
  }
};

/**
 * FLAGS: identity
 * The returned data-URI encodes the account's TOTP shared key. Treat it as a
 * credential: never log it, persist it, or send it anywhere but the <img> tag.
 * Authenticator enrollment, step 1: returns the otpauth QR as a data-URI
 * image, or null when the account already has an authenticator enrolled
 * (the API answers `false` in that case).
 */
export const getTwoFactorRegistrationInfo = async (): Promise<
  string | null
> => {
  const { data } = await apiClient.get<{ qrCodeSetupImageUrl: string } | false>(
    "/v1/users/2fa/authenticator/registration-info",
    {
      skipAuthRefresh: true,
    },
  );
  return data === false ? null : data.qrCodeSetupImageUrl;
};

/**
 * Authenticator enrollment, step 2: marks the authenticator as enabled after
 * the user scanned the QR. The first code verification (step 3) goes through
 * the regular validateTotp call.
 */
export const enableAuthenticator = async (): Promise<void> => {
  await apiClient.patch(
    "/v1/users/2fa/authenticator/enable",
    {},
    { skipAuthRefresh: true },
  );
};

export const logout = async (): Promise<void> => {
  await apiClient.post("/v1/users/logout", undefined, {
    skipAuthRefresh: true,
  });
};
